import React, { useEffect, useState, useRef } from 'react';
import IkIslemleriAPI, { PerSicilZimmetListRequest } from '../../../apis/IkIslemleriAPI';
import TableComponent from "../../../components/OB/TableComponent";

interface DemirbasZimmetListesiProps {
    qtSicilId: number;
    personelTipi: string;
}

const DemirbasZimmetListesi: React.FC<DemirbasZimmetListesiProps> = ({ qtSicilId, personelTipi }) => {
    const [zimmetList, setZimmetList] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // API istek kontrolü - aynı anda birden fazla istek engelleme
    const isLoadingRef = useRef(false);
    // Son yüklenen sicilId - gereksiz tekrar yüklemeyi önle
    const lastLoadedSicilRef = useRef<number | null>(null);

    type SortField = "fisNo" | "verilisTarihi" | null;

    const [sortField, setSortField] = useState<SortField>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const sortedData = React.useMemo(() => {
        if (!sortField) return zimmetList;

        return [...zimmetList].sort((a, b) => {
            let valA: any;
            let valB: any;

            if (sortField === "fisNo") {
                valA = (a.fisNo ?? "").toString();
                valB = (b.fisNo ?? "").toString();

                return sortDirection === "asc"
                    ? valA.localeCompare(valB, 'tr')
                    : valB.localeCompare(valA, 'tr');
            }

            if (sortField === "verilisTarihi") {
                valA = new Date(a.verilisTarihiTs).getTime();
                valB = new Date(b.verilisTarihiTs).getTime();

                return sortDirection === "asc"
                    ? valA - valB
                    : valB - valA;
            }

            return 0;
        });
    }, [zimmetList, sortField, sortDirection]);

    const renderSortIcon = (field: SortField) => {
        if (sortField !== field) {
            return <span style={{ marginLeft: 6, opacity: 0.3 }}>⇅</span>;
        }

        return sortDirection === "asc"
            ? <span style={{ marginLeft: 6 }}>▲</span>
            : <span style={{ marginLeft: 6 }}>▼</span>;
    };

    const columns = [
        {
            header: (
                <div
                    onClick={() => {
                        setSortField("fisNo");
                        setSortDirection(prev =>
                            sortField === "fisNo" && prev === "asc" ? "desc" : "asc"
                        );
                    }}
                    style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        userSelect: "none"
                    }}
                >
                    Fiş No
                    {renderSortIcon("fisNo")}
                </div>
            ) as any,
            field: "fisNo",
            className: "text-center"
        },
        {
            header: (
                <div
                    onClick={() => {
                        setSortField("verilisTarihi");
                        setSortDirection(prev =>
                            sortField === "verilisTarihi" && prev === "asc" ? "desc" : "asc"
                        );
                    }}
                    style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        userSelect: "none"
                    }}
                >
                    Veriliş Tarihi
                    {renderSortIcon("verilisTarihi")}
                </div>
            ) as any,
            field: "verilisTarihiText",
            className: "text-center"
        },
        { header: "Depo No", field: "depoNo", className: "text-center" },
        { header: "Yer Kodu", field: "yerKodu", className: "text-center" },
        { header: "Kurumsal Kodu", field: "kurumsalKodu", className: "text-center" },
        { header: "Adet", field: "adet", className: "text-center" },
        { header: "Sicil Numarası", field: "sicilNumarasi", className: "text-center" },
        { header: "Kodu", field: "malzemeKodu", className: "text-center" },
        { header: "Adı", field: "malzemeAdi", className: "text-center" },
    ];

    // Tarih formatlama fonksiyonu
    const formatDate = (dateString: any): string => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "Bilgi Yok";
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        } catch {
            return "Bilgi Yok";
        }
    };

    useEffect(() => {
        const fetchDemirbasZimmetListesi = async () => {
            // Rate limiting kontrolü - aynı istek devam ediyorsa veya aynı sicil zaten yüklendiyse çık
            if (isLoadingRef.current) {
                return;
            }
            if (lastLoadedSicilRef.current === qtSicilId && zimmetList.length > 0) {
                return;
            }

            setError(null);
            if (!qtSicilId) {
                setZimmetList([]);
                setError("Sicil ID mevcut değil.");
                setLoading(false);
                return;
            }
            try {
                isLoadingRef.current = true;
                setLoading(true);

                // personelTipi'ne göre personelTur değerini belirle
                let personelTurValue: number;
                if (personelTipi === 'M') {
                    personelTurValue = 1; // Memur
                } else if (personelTipi === 'I' || personelTipi === 'K' || personelTipi === 'T') {
                    personelTurValue = 2; // İşçi / Taşeron
                } else {
                    personelTurValue = 1; // Varsayılan
                }

                const request: PerSicilZimmetListRequest = {
                    qtSicilId: qtSicilId,//3139,
                    personelTur: personelTurValue//2
                };
                const response = await IkIslemleriAPI.perSicilZimmetList(request);
                if (response && response.data) {
                    const formattedData = response.data.map((item: any) => ({
                        ...item,
                        verilisTarihiTs: new Date(item.verilisTarihi).getTime(),

                        verilisTarihiText: formatDate(item.verilisTarihi),
                    }));
                    setZimmetList(formattedData);
                    lastLoadedSicilRef.current = qtSicilId;
                } else {
                    setZimmetList([]);
                }
            } catch (err: any) {
                setZimmetList([]);
                setError("Veri çekilirken bir hata oluştu: " + (err?.message ?? err));
                console.error("Demirbaş Zimmet Listesi Hatası:", err);
            } finally {
                isLoadingRef.current = false;
                setLoading(false);
            }
        };
        fetchDemirbasZimmetListesi();
    }, [qtSicilId, personelTipi]);

    const colCount = 9;

    return (
        <TableComponent
            columns={columns}
            data={sortedData}
            recordsPerPage={12}
        />
    );
};

export default DemirbasZimmetListesi;
